import { useCallback, useMemo } from "react";
import { useLocalSearchParams, usePathname, useRouter } from "expo-router";
// @ts-expect-error baseline 2026-02-16
import { WebLayout } from "@src/components/web";
import { View } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { useSites } from "@src/features/cameras/hooks/useSites";
import { useTenantId } from "@src/features/tenants";
import { useTrafficFlowScenarios } from "../hooks";
import { buildWorklist, type Worklist as WorklistData, type WorklistScenarioRow } from "../utils/worklist";
import { WorklistHeader } from "../components/WorklistHeader.web";
import { WorklistFilterToggle, type WorklistFilter } from "../components/WorklistFilterToggle.web";
import { Worklist } from "../components/Worklist.web";

/**
 * Traffic Flow entry point (config-redesign design doc §0/§4): the "Traffic Flow"
 * menu item routes straight here for the selected tenant. Shows tenant-level
 * metrics up top, then a text-only worklist grouped by site. The old map/grid
 * screen is replaced — the map now lives on a dedicated config page reached via
 * the row actions.
 */
export default function TrafficFlowWorklistScreen() {
  const tenantId = useTenantId();
  const {
    data: scenarios,
    isLoading: isScenariosLoading,
    isFetching: isScenariosFetching,
    error: scenariosError,
    refetch: refetchScenarios,
    isTenantPending,
  } = useTrafficFlowScenarios();
  // Sites only enrich site name/address; the worklist falls back to the raw site
  // id when absent, so we don't gate the primary loading/error state on them.
  const { sites } = useSites();

  const worklist = useMemo(() => (scenarios ? buildWorklist(scenarios, sites) : undefined), [scenarios, sites]);

  const router = useRouter();
  const pathname = usePathname();
  const { filter: filterParam } = useLocalSearchParams<{ filter?: string }>();

  // Data-driven default: land on "Missing only" when there's work to do, else
  // "All" so a fully-configured tenant isn't shown an empty page. An explicit URL
  // param always wins so the operator's choice survives refresh/back.
  const filter: WorklistFilter = useMemo(() => {
    if (filterParam === "all") return "all";
    if (filterParam === "missing") return "missing";
    return worklist && worklist.counts.missing > 0 ? "missing" : "all";
  }, [filterParam, worklist]);

  const setFilter = useCallback(
    (next: WorklistFilter) => {
      const params = new URLSearchParams({ filter: next });
      router.replace(`${pathname}?${params.toString()}` as never);
    },
    [pathname, router],
  );

  const visibleGroups = useMemo(() => {
    if (!worklist) return undefined;
    if (filter === "all") return worklist.groups;
    // Missing-only: drop configured rows, then drop sites left with nothing —
    // "done" sites shouldn't clutter the operator's outstanding-work view.
    return worklist.groups
      .map((group) => ({ ...group, rows: group.rows.filter((row) => row.status === "missing") }))
      .filter((group) => group.rows.length > 0);
  }, [worklist, filter]);

  const onViewSiteOnMap = useCallback(
    (siteId: string) => {
      const params = new URLSearchParams({ site: siteId });
      router.push(`/tenants/${tenantId}/traffic-flow/configure?${params.toString()}` as never);
    },
    [router, tenantId],
  );

  const onConfigureScenario = useCallback(
    (row: WorklistScenarioRow) => {
      const params = new URLSearchParams({ site: row.siteId, scenario: row.scenarioUuid });
      router.push(`/tenants/${tenantId}/traffic-flow/configure?${params.toString()}` as never);
    },
    [router, tenantId],
  );

  return (
    <WebLayout>
      <View flex={1} style={{ paddingTop: 16, paddingHorizontal: 24, gap: 16, minHeight: 0 }}>
        {worklist ?
          <>
            <WorklistHeader counts={worklist.counts} />
            <WorklistFilterToggle
              value={filter}
              onChange={setFilter}
              missingCount={worklist.counts.missing}
              totalCount={worklist.counts.total}
            />
          </>
        : null}
        <View flex={1} style={{ minHeight: 0 }}>
          <DataStateView<WorklistData["groups"]>
            data={visibleGroups}
            isLoading={isTenantPending || isScenariosLoading}
            isFetching={isScenariosFetching}
            error={scenariosError ?? null}
            refetch={() => {
              void refetchScenarios();
            }}
            isEmpty={(g) => g.reduce((sum, group) => sum + group.rows.length, 0) === 0}
            loadingMessage="Loading scenarios..."
            errorTitle="Unable to load scenarios"
            emptyState={
              filter === "missing" ?
                {
                  icon: "checkmark",
                  title: "Nothing missing configuration",
                  message: "Every scenario in this tenant has a pin and a bearing. Switch to All to review them.",
                }
              : {
                  icon: "motion",
                  title: "No traffic-flow scenarios reported yet",
                  message: "Cameras configured for traffic-flow will appear here once they publish scenario status.",
                }
            }
          >
            {(groups) => (
              <Worklist groups={groups} onViewSiteOnMap={onViewSiteOnMap} onConfigureScenario={onConfigureScenario} />
            )}
          </DataStateView>
        </View>
      </View>
    </WebLayout>
  );
}
